import { authenticatedFetch } from './apiUtils';
import { API_URL } from '../config';

interface Hike {
  _id: string;
  name: string;
  country: string;
  difficulty: 'Easy' | 'Moderate' | 'Hard' | 'Very Hard';
  length: number;
  elevation: number;
  rating: number;
  rank: number;
  link?: string;
  userId: string;
  likes: string[];
  isLiked: boolean;
  completionCount?: number;
  notes?: string;
  completedOn?: string;
  weatherConditions?: string[];
  photo?: {
    url: string;
    uploadedAt: string;
  };
  gpxFile?: {
    s3Key: string;
    fileName: string;
    fileSize: number;
    uploadDate: string;
    isShared?: boolean;
  };
  isWishList: boolean;
  user: string;
}

// Cache duration (5 minutes)
const CACHE_DURATION = 5 * 60 * 1000;

// Cache keys
const CACHE_KEYS = {
  MY_HIKES: 'my-hikes',
  USER_HIKES: 'user-hikes',
  WISHLIST: 'wishlist',
  TOP_HIKES: 'top-hikes',
  FRIENDS_HIKES: 'friends-hikes'
} as const;

const CACHE_PREFIX = 'hikes-cache-';

const isQuotaExceeded = (error: unknown): boolean =>
  error instanceof DOMException &&
  (error.name === 'QuotaExceededError' || error.name === 'NS_ERROR_DOM_QUOTA_REACHED');

/** Remove every localStorage key that starts with the hike cache prefix (includes user-specific keys). */
const clearAllHikeCacheKeys = (): void => {
  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const storageKey = localStorage.key(i);
    if (storageKey?.startsWith(CACHE_PREFIX)) {
      keysToRemove.push(storageKey);
    }
  }
  keysToRemove.forEach((storageKey) => localStorage.removeItem(storageKey));
};

/** Drop non-essential caches that commonly fill the ~5MB localStorage quota. */
const freeLocalStorageSpace = (): void => {
  clearAllHikeCacheKeys();
  localStorage.removeItem('hikeCoordinatesCache');
  [
    'hikeFormBackup',
    'wishlistFormBackup',
    'editHikeFormBackup',
    'editWishlistFormBackup',
    'loginFormBackup',
    'registerFormBackup',
    'socialLinksFormBackup',
    'passwordChangeFormBackup',
  ].forEach((key) => localStorage.removeItem(key));
};

// Get cached data
const getCachedData = (key: string): any => {
  try {
    const cached = localStorage.getItem(`${CACHE_PREFIX}${key}`);
    if (cached) {
      const { data, timestamp } = JSON.parse(cached);
      // Check if cache is still valid
      if (Date.now() - timestamp < CACHE_DURATION) {
        return data;
      }
    }
  } catch (error) {
    console.error('Error reading cached data:', error);
  }
  return null;
};

// Set cached data (best-effort; never throw on quota)
const setCachedData = (key: string, data: any): void => {
  const serialized = JSON.stringify({
    data,
    timestamp: Date.now(),
  });
  const storageKey = `${CACHE_PREFIX}${key}`;

  try {
    localStorage.setItem(storageKey, serialized);
  } catch (error) {
    if (!isQuotaExceeded(error)) {
      console.error('Error setting cached data:', error);
      return;
    }

    // Evict other caches and retry once; skip if still over quota
    try {
      freeLocalStorageSpace();
      localStorage.setItem(storageKey, serialized);
    } catch {
      // Cache is optional — app continues without it
    }
  }
};

// Clear cache
const clearCache = (key?: string): void => {
  if (key) {
    localStorage.removeItem(`${CACHE_PREFIX}${key}`);
  } else {
    clearAllHikeCacheKeys();
  }
};

// Fetch my hikes with caching
export const fetchMyHikes = async (): Promise<Hike[]> => {
  // Check cache first
  const cached = getCachedData(CACHE_KEYS.MY_HIKES);
  if (cached) {
    return cached;
  }

  // Fetch from API
  const response = await authenticatedFetch(`${API_URL}/hikes/my-hikes`);
  if (!response.ok) {
    throw new Error('Failed to fetch my hikes');
  }
  
  const data = await response.json();
  
  // Cache the data
  setCachedData(CACHE_KEYS.MY_HIKES, data);
  
  return data;
};

// Fetch user hikes with caching
export const fetchUserHikes = async (userId: string): Promise<Hike[]> => {
  const cacheKey = `${CACHE_KEYS.USER_HIKES}-${userId}`;
  
  // Check cache first
  const cached = getCachedData(cacheKey);
  if (cached) {
    return cached;
  }

  // Fetch from API
  const response = await authenticatedFetch(`${API_URL}/hikes/user/${userId}`);
  if (!response.ok) {
    throw new Error('Failed to fetch user hikes');
  }
  
  const data = await response.json();
  
  // Cache the data
  setCachedData(cacheKey, data);
  
  return data;
};

// Fetch wishlist with caching
export const fetchWishlist = async (): Promise<Hike[]> => {
  // Check cache first
  const cached = getCachedData(CACHE_KEYS.WISHLIST);
  if (cached) {
    return cached;
  }

  // Fetch from API
  const response = await authenticatedFetch(`${API_URL}/hikes/my-wishlist`);
  if (!response.ok) {
    throw new Error('Failed to fetch wishlist');
  }
  
  const data = await response.json();
  
  // Cache the data
  setCachedData(CACHE_KEYS.WISHLIST, data);
  
  return data;
};

// Fetch top hikes with caching
export const fetchTopHikes = async (): Promise<Hike[]> => {
  // Check cache first
  const cached = getCachedData(CACHE_KEYS.TOP_HIKES);
  if (cached) {
    return cached;
  }

  // Fetch from API
  const response = await authenticatedFetch(`${API_URL}/hikes/top`);
  if (!response.ok) {
    throw new Error('Failed to fetch top hikes');
  }
  
  const data = await response.json();
  
  // Cache the data
  setCachedData(CACHE_KEYS.TOP_HIKES, data);
  
  return data;
};

// Fetch friends hikes with caching
export const fetchFriendsHikes = async (): Promise<Hike[]> => {
  // Check cache first
  const cached = getCachedData(CACHE_KEYS.FRIENDS_HIKES);
  if (cached) {
    return cached;
  }

  // Fetch from API
  const response = await authenticatedFetch(`${API_URL}/hikes/friends`);
  if (!response.ok) {
    throw new Error('Failed to fetch friends hikes');
  }
  
  const data = await response.json();
  
  // Cache the data
  setCachedData(CACHE_KEYS.FRIENDS_HIKES, data);
  
  return data;
};

// Add hike and invalidate cache
export const addHike = async (hikeData: any): Promise<Hike> => {
  const response = await authenticatedFetch(`${API_URL}/hikes`, {
    method: 'POST',
    body: JSON.stringify(hikeData)
  });
  
  if (!response.ok) {
    throw new Error('Failed to add hike');
  }
  
  const data = await response.json();
  
  // Invalidate relevant caches
  clearCache();
  
  return data;
};

// Update hike and invalidate cache
export const updateHike = async (hikeId: string, updates: any): Promise<Hike> => {
  const response = await authenticatedFetch(`${API_URL}/hikes/${hikeId}`, {
    method: 'PATCH',
    body: JSON.stringify(updates)
  });
  
  if (!response.ok) {
    throw new Error('Failed to update hike');
  }
  
  const data = await response.json();
  
  // Invalidate relevant caches
  clearCache();
  
  return data;
};

// Delete hike and invalidate cache
export const deleteHike = async (hikeId: string): Promise<any> => {
  const response = await authenticatedFetch(`${API_URL}/hikes/${hikeId}`, {
    method: 'DELETE'
  });
  
  if (!response.ok) {
    throw new Error('Failed to delete hike');
  }
  
  const data = await response.json();
  
  // Invalidate relevant caches
  clearCache();
  
  return data;
};

// Manual cache refresh
export const refreshCache = (key?: string): void => {
  clearCache(key);
};

// Export cache utilities
export { clearCache, getCachedData, setCachedData, CACHE_KEYS };
